import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { GenesysFn, TranslationKeys } from 'src/app/contracts';
import { Auth } from 'src/app/core/common/auth';
import { backButton$ } from 'src/app/core/contracts';
import { Logger } from 'src/app/core/helpers/logger';
import { UI } from 'src/app/core/helpers/ui';
import { isDev } from 'src/app/core/helpers/utils';

@Injectable({ providedIn: 'root' })
export class GenesysService {
  private document = inject(DOCUMENT);
  private alertCtrl = inject(AlertController);
  private logger = inject(Logger);
  private auth = inject(Auth);
  private ui = inject(UI);
  private loaded = false;
  private readonly scriptSrc = 'https://apps.mypurecloud.ie/genesys-bootstrap/genesys.min.js';
  private readonly config = Object.freeze({
    environment: 'prod-euw1',
    deploymentId: '56272c29-b410-4077-ba19-f53f01315414',
  });

  public init(): void {
    if (isDev) {
      return;
    }
    if (this.loaded) {
      return;
    }
    const w = window;
    if (w.Genesys && (w.Genesys as GenesysFn).t) {
      // Already bootstrapped by something else
      this.loaded = true;
      return;
    }
    this.bootstrap(w);
  }
  private bootstrap(w: Window): void {
    /**
     *  - We expose a lightweight placeholder function (window.Genesys) immediately so app code can invoke Genesys API calls
     *    without waiting for the external SDK network fetch.
     *  - Each invocation's arguments are pushed into an in‑memory queue (Genesys.q). When the real Genesys script finishes
     *    loading, it detects an existing queue and replays those calls in order, preserving intent and call sequence.
     *  - This avoids race conditions (calls before load are not lost or causing errors) and lets us lazy / async load the
     *    vendor script without blocking initial rendering.
     *  - We also stamp a timestamp (t) and config (c) properties which many vendor bootstraps read for performance metrics
     *    and initial configuration before processing the queued calls.
     *  - Idempotent: if another part of the app (or a tag manager) already created / loaded Genesys, we detect it and skip.
     */
    // Create queueing function if not existing
    if (!w.Genesys) {
      const genesysFn: GenesysFn = (...args: unknown[]) => {
        (genesysFn.q = genesysFn.q || []).push(args);
      };
      genesysFn.t = Date.now();
      genesysFn.c = this.config;
      w._genesysJs = 'Genesys';
      w.Genesys = genesysFn;
    }

    // Inject script tag only once
    if (!this.document.querySelector('script[src="' + this.scriptSrc + '"]')) {
      const scriptEl = this.document.createElement('script');
      scriptEl.async = true;
      scriptEl.src = this.scriptSrc;
      scriptEl.charset = 'utf-8';
      // Genesys recommends placing the bootstrap in <head>
      this.document.head.appendChild(scriptEl);
    }
    this.loaded = true;
  }
  async promptForPhoneAndOpenChat() {
    let phone = await this.getCustomerPhone();
    if (!phone) {
      return;
    }
    if (phone.length == 9) {
      phone = '966' + phone;
    }
    this.openChat(phone);
  }
  async getCustomerPhone() {
    const user = await firstValueFrom(this.auth.user$);
    if (user) {
      return user.preferred_username.toString();
    }
    return await this.promptForPhoneNumber();
  }
  promptForPhoneNumber() {
    return new Promise<string | null>(async resolve => {
      const alert = await this.alertCtrl.create({
        header: this.ui.translate.instant(TranslationKeys.HomePage.EnterPhoneNumber),
        cssClass: 'genesys-alert',
        inputs: [
          {
            name: 'phoneNumber',
            type: 'number',
            placeholder: this.ui.translate.instant(TranslationKeys.HomePage.PhonePlaceholder),
          },
        ],
        buttons: [
          {
            text: this.ui.translate.instant(TranslationKeys.Common.Cancel),
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {},
          },
          {
            text: this.ui.translate.instant(TranslationKeys.HomePage.StartConversation),
            role: 'confirm',
            handler: (data: { phoneNumber: string }) => {
              if (!data.phoneNumber || !/^\d{9}$/.test(data.phoneNumber)) {
                this.ui.showError(TranslationKeys.HomePage.InvalidPhone);
                return false;
              }
              return true;
            },
          },
        ],
      });
      await alert.present();
      const result = await alert.onDidDismiss<{ values: { phoneNumber: string } }>();
      if (result.role === 'confirm') {
        resolve(result.data.values.phoneNumber);
      } else {
        resolve(null);
      }
    });
  }
  openChat(phoneNumber: string) {
    if (isDev) {
      this.ui.showError(TranslationKeys.Genesys.ChatIsNotEnabledOnDevSeeGenesysService);
      return;
    }
    const genesys = window.Genesys;
    genesys(
      'command',
      'Database.set',
      { messaging: { customAttributes: { mobile: phoneNumber, name: phoneNumber } } },
      () => {
        genesys(
          'command',
          'Messenger.open',
          {},
          () => {},
          err => {
            this.logger.error({ msg: 'Failed to open genesys messenger window', err });
            genesys('command', 'Messenger.close');
          }
        );

        const subscription = backButton$.subscribe(() => {
          genesys('command', 'Messenger.close');
          subscription.unsubscribe();
        });
      },
      err => {
        this.ui.showError(TranslationKeys.Genesys.FailedToConfigureChat);
        this.logger.error({ msg: 'Failed to set genesys custom attributes', err });
      }
    );
  }
}
